import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  AdAssetViewItem,
  GenerateSuggestionsResponseItem,
  generateSuggestions,
  updateAdGroupAdAssets,
} from '~/services/PublicOfferFeedService';

type AdAssetViewItemWithAdGroupAdAssetViewId = AdAssetViewItem & {
  adGroupAdAssetViewId?: string;
  id?: string;
};

export type SuggestionItem = {
  assetId: string;
  adGroupAdId: string;
  adGroupAdAssetViewId: string;
  originalText: string;
  assetType: string;
  suggestions: Array<string>;
  editedSuggestions: Array<string>;
  selectedForPublish: Set<number>;
};

const ASSET_TYPE_LABELS: Record<string, string> = {
  HEADLINE: 'Headline',
  DESCRIPTION: 'Description',
};

const DEFAULT_ASSET_TYPE_LABEL = 'Description';

const getAdGroupAdAssetViewId = (item: AdAssetViewItem): string => {
  const itemWithId = item as AdAssetViewItemWithAdGroupAdAssetViewId;
  return itemWithId.adGroupAdAssetViewId || itemWithId.id || '';
};

const createSuggestionItemKey = (adGroupAdId: string, assetId: string): string => {
  return `${adGroupAdId}-${assetId}`;
};

const createEmptySuggestionItem = (item: AdAssetViewItem): SuggestionItem => ({
  assetId: item.assetId,
  adGroupAdId: item.adGroupAdId,
  adGroupAdAssetViewId: getAdGroupAdAssetViewId(item),
  originalText: item.textAsset,
  assetType: item.fieldType,
  suggestions: [],
  editedSuggestions: [],
  selectedForPublish: new Set<number>(),
});

interface AIGeneratedSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  items: Array<AdAssetViewItem>;
  customerId: string;
  campaignId: string;
  adGroupId: string;
  onSaveSuccess?: () => void;
}

export default function AIGeneratedSuggestionsModal({
  open,
  onClose,
  items,
  customerId,
  campaignId,
  adGroupId,
  onSaveSuccess,
}: AIGeneratedSuggestionsModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [suggestionItems, setSuggestionItems] = useState<Array<SuggestionItem>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState<string | null>(null);

  const initializeSuggestions = useCallback(async () => {
    if (!open || items.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      const requestPayload = {
        items: items.map((item) => ({
          assetId: item.assetId,
          adGroupAdId: item.adGroupAdId,
          adGroupAdAssetViewId: getAdGroupAdAssetViewId(item),
          text: item.textAsset,
          assetType: item.fieldType,
        })),
      };

      const response = await generateSuggestions(customerId, campaignId, adGroupId, requestPayload);

      if (!response?.result || !Array.isArray(response.result)) {
        throw new Error('Invalid response format from API');
      }

      const resultsMap = new Map<string, GenerateSuggestionsResponseItem>();
      response.result.forEach((result) => {
        if (result?.assetId && result?.adGroupAdId) {
          const key = createSuggestionItemKey(result.adGroupAdId, result.assetId);
          resultsMap.set(key, result);
        }
      });

      const suggestionItems = items.map((item) => {
        const key = createSuggestionItemKey(item.adGroupAdId, item.assetId);
        const result = resultsMap.get(key);

        const suggestedText = result?.suggestedText || '';
        const suggestions = suggestedText ? [suggestedText] : [];

        return {
          ...createEmptySuggestionItem(item),
          suggestions,
          editedSuggestions: [...suggestions],
        } as SuggestionItem;
      });

      setSuggestionItems(suggestionItems);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate suggestions';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      const emptySuggestions = items.map(createEmptySuggestionItem);
      setSuggestionItems(emptySuggestions);
    } finally {
      setIsLoading(false);
    }
  }, [open, items, customerId, campaignId, adGroupId, enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      initializeSuggestions();
    } else {
      setSuggestionItems([]);
    }
  }, [open, initializeSuggestions]);

  const handleSuggestionEdit = useCallback((itemIndex: number, suggestionIndex: number, newText: string) => {
    setSuggestionItems((prev) => {
      const updated = [...prev];
      if (!updated[itemIndex]) {
        return prev;
      }
      updated[itemIndex] = {
        ...updated[itemIndex],
        editedSuggestions: updated[itemIndex].editedSuggestions.map((text, idx) =>
          idx === suggestionIndex ? newText : text,
        ),
      };
      return updated;
    });
  }, []);

  const handleRegenerate = useCallback(
    async (itemIndex: number) => {
      const item = suggestionItems[itemIndex];
      if (!item) {
        return;
      }

      setIsRegenerating(item.assetId);
      try {
        const requestPayload = {
          items: [
            {
              assetId: item.assetId,
              adGroupAdId: item.adGroupAdId,
              adGroupAdAssetViewId: item.adGroupAdAssetViewId || '',
              text: item.originalText,
              assetType: item.assetType,
            },
          ],
        };

        const response = await generateSuggestions(customerId, campaignId, adGroupId, requestPayload);

        if (!response?.result || !Array.isArray(response.result) || response.result.length === 0) {
          throw new Error('Invalid response format from API');
        }

        const result = response.result[0];
        const suggestedText = result?.suggestedText || '';
        const suggestions = suggestedText ? [suggestedText] : [];

        setSuggestionItems((prev) => {
          const updated = [...prev];
          if (!updated[itemIndex]) {
            return prev;
          }
          updated[itemIndex] = {
            ...updated[itemIndex],
            suggestions,
            editedSuggestions: [...suggestions],
            selectedForPublish: new Set<number>(),
          };
          return updated;
        });
        enqueueSnackbar('Suggestions regenerated successfully', { variant: 'success' });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to regenerate suggestions';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setIsRegenerating(null);
      }
    },
    [suggestionItems, customerId, campaignId, adGroupId, enqueueSnackbar],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const assets = suggestionItems
        .filter((item) => item.editedSuggestions.length > 0)
        .map((item) => ({
          assetId: item.assetId,
          text: item.editedSuggestions[0],
          assetType: item.assetType,
          adId: item.adGroupAdId,
        }));

      if (assets.length > 0) {
        await updateAdGroupAdAssets(customerId, campaignId, adGroupId, {
          assets,
        });
      }

      enqueueSnackbar('Assets updated successfully', { variant: 'success' });
      onSaveSuccess?.();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save assets';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [suggestionItems, enqueueSnackbar, onSaveSuccess, onClose]);

  const getAssetTypeLabel = useCallback((assetType: string): string => {
    return ASSET_TYPE_LABELS[assetType] || DEFAULT_ASSET_TYPE_LABEL;
  }, []);

  const hasValidationErrors = useCallback((): boolean => {
    return suggestionItems.some((item) => {
      const maxLength = item.assetType === 'HEADLINE' ? 30 : 90;
      return item.editedSuggestions.some((suggestion) => suggestion.length > maxLength);
    });
  }, [suggestionItems]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">AI Generated Suggestions</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : suggestionItems.length === 0 ? (
          <Typography>No suggestions available</Typography>
        ) : (
          <Stack spacing={4}>
            {suggestionItems.map((item, itemIndex) => {
              const itemKey = createSuggestionItemKey(item.adGroupAdId, item.assetId);
              const isItemRegenerating = isRegenerating === item.assetId;
              const chipColor = item.assetType === 'HEADLINE' ? 'primary' : 'secondary';

              return (
                <Box key={itemKey}>
                  <Box mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip label={getAssetTypeLabel(item.assetType)} color={chipColor} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        Asset ID: {item.assetId}
                      </Typography>
                    </Stack>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Original Text
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          minHeight: 100,
                        }}
                      >
                        <Typography variant="body2">{item.originalText}</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        AI Generated Suggestions
                      </Typography>
                      <Stack spacing={2}>
                        {item.editedSuggestions.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No suggestions available
                          </Typography>
                        ) : (
                          item.editedSuggestions.map((suggestion, suggestionIndex) => {
                            const maxLength = item.assetType === 'HEADLINE' ? 30 : 90;
                            const isExceedingLimit = suggestion.length > maxLength;
                            return (
                              <TextField
                                key={`${itemKey}-suggestion-${suggestionIndex}`}
                                fullWidth
                                multiline
                                rows={3}
                                value={suggestion}
                                onChange={(e) => handleSuggestionEdit(itemIndex, suggestionIndex, e.target.value)}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength }}
                                error={isExceedingLimit}
                                helperText={
                                  isExceedingLimit
                                    ? `Character limit exceeded. Maximum ${maxLength} characters allowed.`
                                    : `${suggestion.length}/${maxLength} characters`
                                }
                              />
                            );
                          })
                        )}
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }}>
                      <Box display="flex" alignItems="flex-start" pt={4}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleRegenerate(itemIndex)}
                          disabled={isItemRegenerating}
                          aria-label="Regenerate"
                        >
                          {isItemRegenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  {itemIndex < suggestionItems.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaving || hasValidationErrors()}>
          {isSaving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
